// Kamera auf Kugelkoordinaten um einen Zielpunkt. Fahrten per gsap, Maus: Ziehen dreht, Rad zoomt.
import * as THREE from "three"
import gsap from "gsap"

export const VIEWS = {
  factory: { target: [0, 2, 0], distance: 58, polar: 1.0, azimuth: 0.55 },
  factoryClose: { target: [0, 7, 0], distance: 78, polar: 1.08, azimuth: 0.4 },
  world: { target: [-2, 0, 0], distance: 205, polar: 0.82, azimuth: 0.35 },
  attract: { target: [0, 0, 0], distance: 190, polar: 0.9, azimuth: 0 }
}

export class CameraRig {
  constructor(camera, element) {
    this.camera = camera
    this.element = element
    this.state = { tx: 0, ty: 0, tz: 0, distance: 200, polar: 0.9, azimuth: 0 }
    this.limits = { minDistance: 30, maxDistance: 240 }
    this.interactive = false
    this.autoRotate = 0
    this.onZoomOut = null
    this.bindInput()
    this.apply()
  }

  bindInput() {
    let dragging = null
    this.element.addEventListener("pointerdown", (event) => {
      if (!this.interactive || event.target.closest("[data-ui]")) return
      dragging = { x: event.clientX, y: event.clientY, moved: 0 }
    })
    window.addEventListener("pointermove", (event) => {
      if (!dragging) return
      const dx = event.clientX - dragging.x
      const dy = event.clientY - dragging.y
      dragging.moved += Math.abs(dx) + Math.abs(dy)
      dragging.x = event.clientX
      dragging.y = event.clientY
      this.state.azimuth -= dx * 0.004
      this.state.polar = THREE.MathUtils.clamp(this.state.polar - dy * 0.003, 0.45, 1.25)
      this.apply()
    })
    window.addEventListener("pointerup", () => {
      this.lastDragDistance = dragging?.moved ?? 0
      dragging = null
    })
    this.element.addEventListener("wheel", (event) => {
      if (!this.interactive) return
      event.preventDefault()
      const factor = Math.exp(event.deltaY * 0.0012)
      const next = this.state.distance * factor
      if (next > this.limits.maxDistance && this.onZoomOut) this.onZoomOut()
      this.state.distance = THREE.MathUtils.clamp(next, this.limits.minDistance, this.limits.maxDistance)
      this.apply()
    }, { passive: false })
  }

  wasDrag() {
    return (this.lastDragDistance ?? 0) > 6
  }

  apply() {
    const { tx, ty, tz, distance, polar, azimuth } = this.state
    const target = new THREE.Vector3(tx, ty, tz)
    const offset = new THREE.Vector3().setFromSphericalCoords(distance, polar, azimuth)
    this.camera.position.copy(target).add(offset)
    this.camera.lookAt(target)
  }

  fly(view, { duration = 2.2, ease = "power2.inOut" } = {}) {
    const [tx, ty, tz] = view.target
    gsap.killTweensOf(this.state)
    // kürzester Weg um die Hochachse
    let azimuth = view.azimuth
    while (azimuth - this.state.azimuth > Math.PI) azimuth -= Math.PI * 2
    while (azimuth - this.state.azimuth < -Math.PI) azimuth += Math.PI * 2
    return new Promise((resolve) => {
      gsap.to(this.state, {
        tx, ty, tz, distance: view.distance, polar: view.polar, azimuth, duration, ease,
        onUpdate: () => this.apply(),
        onComplete: resolve
      })
    })
  }

  flyToPoint(point, { distance = 62, polar = 1.18, duration = 1.6 } = {}) {
    return this.fly({ target: [point.x, point.y + 3, point.z], distance, polar, azimuth: this.state.azimuth }, { duration })
  }

  update(dt) {
    if (this.autoRotate) {
      this.state.azimuth += this.autoRotate * dt
      this.apply()
    }
  }
}
